<template>
  <cs-preloader-overlay :loading="loading">
    <div :class="[$style.overlay, $style[ticketStatusClass]]">
      <div :class="$style.container">

        <template v-if="isNewTicket">
          <div :class="$style.content">
            <div :class="[$style.icon, $style.new]">
              <base-icon name="basic_check-mark" :size="64" />
            </div>
            <div
              :class="$style.text"
              v-text="$t('ticket.code.status.new')"
            />
            <div :class="$style.details">
              <div :class="$style.detail">
                <span v-text="$t('ticket.code.number')" />
                <strong v-text="ticketCode" />
              </div>
              <div :class="$style.detail">
                <span v-text="$t('ticket.code.created.at')" />
                <strong v-text="ticketCreatedAt" />
              </div>
              <div
                v-if="displayDescription"
                :class="$style.detail"
              >
                {{ displayDescription }}
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="isShownTicket">
          <div :class="$style.content">
            <div :class="[$style.icon, $style.shown]">
              <base-icon name="basic_check-marks" :size="64" />
            </div>
            <div
              :class="$style.text"
              v-text="$t('ticket.code.status.shown')"
            />
            <div :class="$style.detail">
              <span v-text="$t('ticket.code.last.shown.at')" />
              <strong v-text="ticketLastShownAt" />
            </div>
            <div :class="$style.details">
              <div :class="$style.detail">
                <span v-text="$t('ticket.code.number')" />
                <strong v-text="ticketCode" />
              </div>
              <div :class="$style.detail">
                <span v-text="$t('ticket.code.created.at')" />
                <strong v-text="ticketCreatedAt" />
              </div>
              <div
                v-if="displayDescription"
                :class="$style.detail"
              >
                {{ displayDescription }}
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div :class="$style.content">
            <div :class="[$style.icon, $style.unknown]">
              <base-icon name="file_clipboard-delete" :size="64" />
            </div>
            <div
              :class="$style.text"
              v-text="$t('ticket.code.status.unknown')"
            />
          </div>
        </template>

      </div>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'TicketCode',
  created() {
    this.loadData();
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    isNewTicket() {
      return this.ticketStatus === 'NEW';
    },
    isShownTicket() {
      return this.ticketStatus === 'SHOWN';
    },
    ticketStatusClass() {
      return this.ticketStatus?.toLowerCase();
    },
    displayDescription() {
      const description = [this.ticketDescription, this.ticketName]
        .filter(i => i)
        .join(', ');

      if (this.$validate.isEmptyString(description)) {
        return null;
      }

      return description;
    },
    ...mapGetters('ticket', [
      'ticketStatus',
      'ticketCode',
      'ticketCreatedAt',
      'ticketLastShownAt',
      'ticketDescription',
      'ticketName',
    ]),
  },
  methods: {
    async loadData() {
      this.loading = true;

      try {
        await this.fetchTicketByCode(this.$route.params.code);
      } finally {
        this.loading = false;
      }
    },
    ...mapActions('ticket', ['fetchTicketByCode']),
  },
};
</script>

<style lang="sass" module>
  .overlay
    position: relative
    min-height: calc(100vh - 60px)
    padding: 0 10px

    &.new
      background-color: var(--color-green)

      &:before
        content: ''
        width: 100%
        height: 100%
        position: absolute
        top: 0
        left: 0
        background-image: url('~images/ticket/ticket-starts-left.svg')
        background-repeat: no-repeat
        background-position: left 0 top 0

      +respond-to('medium')
        &:before
          background-position: left 15vw top 15vh

        &:after
          content: ''
          width: 100%
          height: 100%
          top: 0
          right: 0
          position: absolute
          background-image: url('~images/ticket/ticket-starts-right.svg')
          background-repeat: no-repeat
          background-position: right 15vw top 10vh

    &.shown
      background-color: var(--color-yellow)

    &.unknown
      background-color: var(--color-red)

  .container
    width: 100%
    max-width: 460px
    margin: 0 auto
    padding: 50px 0
    position: relative
    z-index: 100

  .content
    +flexbox
    +flex-direction(column)
    padding: 20px
    background-color: var(--color-dark-blue-3)
    border-radius: 20px

  .icon
    margin: 20px auto
    padding: 20px
    border-radius: 100px

    &.new
      background-color: var(--color-green)

    &.shown
      background-color: var(--color-yellow)
      color: var(--color-dark-blue-3)

    &.unknown
      background-color: var(--color-red)

  .text
    +font-type(semi-bold, 24, 28)
    color: var(--color-white)
    text-align: center

  .details
    margin-top: 20px
    border-top: 1px solid var(--color-white)

  .detail
    +flexbox
    +justify-content(space-between)
    +font-type(regular, 16, 20)
    padding-top: 20px

    span
      white-space: nowrap
      margin-right: 10px

    strong
      +font-weight(bold)
      text-align: right
</style>
